'use strict';

// Managed 2-of-3 recovery, aligned with the SmartLedger Shamir service
// (shamir.smartledger.technology). We split the BIP39 MNEMONIC itself — the single root
// of the identity — into three slices with threshold 2:
//   U = user (saved as a recovery code) · C = web3keys · S = recovery service.
// Any two reconstruct the mnemonic; recovering it restores the WHOLE identity (the
// DID/VC signing key is mnemonic-derived). Splits use the SAME @smartledger/bsv Shamir
// the service uses, so the slice format is byte-identical and the config swap is clean.

const { getBsv } = require('./lib');
const { toB64, fromB64 } = require('./b64');

const THRESHOLD = 2;
const SHARES = 3;

// A Shamir share is an opaque JSON object — @smartledger/bsv v5 (grempe/GF(2^8)) shapes it
// as {v,id,threshold,shares,length,splitId,share,checksum}; v4 used {id,threshold,shares,
// length,bytes:[{x,y}]}. We treat it as opaque (split/combine round-trip either), the service
// stores it as-is; for the USER's slice we hand out a compact base64(JSON) "code".
const encodeShare = (share) => toB64(new TextEncoder().encode(JSON.stringify(share)));
const decodeShare = (s) => (typeof s === 'string' ? JSON.parse(new TextDecoder().decode(fromB64(s))) : s);

/** Split a mnemonic into { user, company, service } raw share objects (2-of-3). */
function splitMnemonic(mnemonic) {
  // Defensive invariant: VERIFY a 2-of-3 reconstruction before trusting the shares, so a corrupt
  // slice can never be persisted/handed out (catastrophic + silent for a recovery secret). The RNG
  // shim that could throw/NaN here was fixed upstream in @smartledger/bsv 5.5.3 (it now re-draws
  // internally instead of returning null), so no retry loop is needed — a bad draw is not expected.
  const bsv = getBsv();
  const shares = bsv.Shamir.split(mnemonic, THRESHOLD, SHARES);
  if (!Array.isArray(shares) || shares.length !== SHARES) throw new Error('Shamir split failed: unexpected share count');
  if (bsv.Shamir.combine([shares[0], shares[2]]).toString('utf8') !== mnemonic) throw new Error('Shamir split failed: round-trip mismatch');
  return { user: shares[0], company: shares[1], service: shares[2] };
}

/** Reconstruct the mnemonic from any two shares (raw objects or base64 codes). */
function combineMnemonic(shareA, shareB) {
  return getBsv().Shamir.combine([decodeShare(shareA), decodeShare(shareB)]).toString('utf8');
}

module.exports = { splitMnemonic, combineMnemonic, encodeShare, decodeShare, THRESHOLD, SHARES };
